import { ReactNode } from "react";
import classNames from "classnames";

export type BadgeVariant = "neutral" | "success" | "warning" | "error" | "info";
export type BadgeSize = "sm" | "md" | "lg";
export type IconPosition = "left" | "right";

export interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: ReactNode;
  iconPosition?: IconPosition;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  neutral: "text-gray-700 bg-gray-400",
  success: "text-green-400 bg-green-100",
  warning: "text-yellow-550 bg-yellow-100",
  error: "text-red-600 bg-red-100",
  info: "text-primary-60 bg-primary-5",
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: "px-1.5 py-0.5 text-xs",
  md: "px-2 py-1 text-xs",
  lg: "px-3 py-1.5 text-sm",
};

export const Badge = ({
  variant = "neutral",
  size = "md",
  icon,
  iconPosition = "left",
  children,
  className,
  ariaLabel,
}: BadgeProps) => {
  return (
    <div
      className={classNames(
        "inline-flex items-center gap-1 rounded font-medium",
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      role="status"
      aria-label={ariaLabel}
    >
      {icon && iconPosition === "left" && icon}
      {children}
      {icon && iconPosition === "right" && icon}
    </div>
  );
};
